import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import {
  STAFF_SESSION_HINT_KEY,
  clearSessionHint,
  fetchSessionMe,
  hasSessionHint,
  setSessionHint,
} from '../utils/authSession';

const StaffAuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

export const StaffAuthProvider = ({ children }) => {
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    if (!hasSessionHint(STAFF_SESSION_HINT_KEY)) {
      setLoading(false);
      return;
    }
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await fetchSessionMe(`${API}/staff/me`);
      if (response.status === 401) {
        clearSessionHint(STAFF_SESSION_HINT_KEY);
        setStaff(null);
        setIsAuthenticated(false);
        return;
      }
      setStaff(response.data);
      setIsAuthenticated(true);
      setSessionHint(STAFF_SESSION_HINT_KEY);
    } catch {
      setStaff(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = (staffData) => {
    setSessionHint(STAFF_SESSION_HINT_KEY);
    setStaff(staffData);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/staff/logout`);
    } catch (error) {
      console.error('Staff logout error:', error);
    } finally {
      clearSessionHint(STAFF_SESSION_HINT_KEY);
      setStaff(null);
      setIsAuthenticated(false);
    }
  };

  const value = {
    staff,
    loading,
    isAuthenticated,
    login,
    logout,
    checkAuthStatus,
  };

  return (
    <StaffAuthContext.Provider value={value}>
      {children}
    </StaffAuthContext.Provider>
  );
};

export const useStaffAuth = () => {
  const ctx = useContext(StaffAuthContext);
  if (!ctx) throw new Error('useStaffAuth must be used within StaffAuthProvider');
  return ctx;
};

export default StaffAuthContext;
